'use client';

import { useState } from 'react';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';
import { useInfiniteQuery } from '@tanstack/react-query';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { BlogCard } from './blog-card';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { Heading } from '../shared/heading';
import { fetchBlogs } from '@eliadmin/core/src/api/blogs/blogs-api';

const ALL_CATEGORIES_TAB = 'all';

export const BlogsList = ({
  locale,
  categories,
}: {
  locale: string;
  categories: { id: number; name: string }[];
}) => {
  const { t } = useTranslations();

  const [activeTab, setActiveTab] = useState(ALL_CATEGORIES_TAB);
  const activeTabId = activeTab === ALL_CATEGORIES_TAB ? undefined : Number(activeTab);

  const blogsQuery = useInfiniteQuery({
    queryKey: [CacheKey.BLOGS, activeTab],
    queryFn: async ({ pageParam = 1 }) => {
      return await fetchBlogs({
        locale,
        query: {
          order: 'desc',
          orderBy: 'created_at',
          results: 6,
          page: pageParam,
          where: activeTabId ? { categories: { equals: activeTabId } } : undefined,
        },
      });
    },
    getNextPageParam: (lastPage: any) => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: 1,
  });

  const blogs = blogsQuery.data?.pages.flatMap((page: any) => page.data) ?? [];

  return (
    <Tabs
      value={activeTab}
      defaultValue={activeTab}
      onValueChange={value => setActiveTab(value)}
      className={cn('custom-container', 'flex flex-col items-start justify-start gap-4')}
    >
      <Heading as="h2" title={t('text3823')} />
      <TabsList
        className={cn(
          'hide-scroll-bar',
          'flex h-fit w-full flex-nowrap items-start justify-start gap-2 overflow-scroll rounded-none bg-transparent !p-0',
        )}
      >
        <TabsTrigger
          value={ALL_CATEGORIES_TAB}
          className={cn(
            'ea-blogs__tabs-trigger',
            'rounded-full px-5 py-2.5 text-sm font-normal shadow-none transition-all',
          )}
        >
          {t('text2000')}
        </TabsTrigger>
        {categories.map(category => (
          <TabsTrigger
            value={String(category.id)}
            key={category.id}
            className={cn(
              'ea-blogs__tabs-trigger',
              'rounded-full px-5 py-2.5 text-sm font-normal shadow-none transition-all',
            )}
          >
            {category.name}
          </TabsTrigger>
        ))}
      </TabsList>
      {[ALL_CATEGORIES_TAB, ...categories.map(category => String(category.id))].map(tabId => (
        <TabsContent key={tabId} value={tabId} forceMount hidden={activeTab !== tabId} className="mt-6 w-full pr-6">
          {activeTab === tabId && <>
          {blogsQuery.error && <p className="pt-8 ">Vyskytla sa chyba.</p>}
          {blogsQuery.isSuccess && blogs.length === 0 && (
            <p className="w-full text-center font-medium">{t('text752')}</p>
          )}
          {/* {blogsQuery.isLoading && <p className="pt-8 ">Načítavam...</p>} */}
          {blogsQuery.isSuccess && blogs.length > 0 && (
            <div className="grid w-full grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2 lg:gap-x-12 lg:gap-y-8 xl:gap-x-16">
              {blogs.map(blog => (
                <BlogCard key={blog.id} blog={blog} />
              ))}
            </div>
          )}
          <div
            className={cn('mt-8 flex w-full items-center justify-center', {
              hidden: !blogsQuery.hasNextPage,
            })}
          >
            <Button
              onClick={() => blogsQuery.fetchNextPage()}
              isLoading={blogsQuery.isFetchingNextPage}
              aria-label={t('text338')}
              className="w-full sm:w-fit"
              variant={'dark'}
            >
              {t('text338')}
            </Button>
          </div>
          </>}
        </TabsContent>
      ))}
    </Tabs>
  );
};

// const ReferencesSkeleton = () => {
//   return (
//     <div
//       className={cn(
//         'animate-pulse',
//         'col-span-1 grid w-full grid-cols-1 gap-8 sm:col-span-2 sm:grid-cols-2 lg:col-span-3 lg:grid-cols-3 lg:gap-10',
//       )}
//     >
//       <ReferenceSkeletonItem />
//       <ReferenceSkeletonItem />
//       <ReferenceSkeletonItem />
//       <ReferenceSkeletonItem />
//       <ReferenceSkeletonItem />
//     </div>
//   );
// };
